import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";

/* ── helpers ──────────────────────────────────────────────── */
function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric", month: "long", year: "numeric",
  });
}

function getLocalTodayStr() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function daysLeft(endIso: string | null | undefined) {
  if (!endIso) return null;
  const todayStr = getLocalTodayStr();
  const endStr = endIso.slice(0, 10);
  
  const todayVal = new Date(todayStr).getTime();
  const endVal = new Date(endStr).getTime();
  const diff = endVal - todayVal;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getStatus(start?: string | null, end?: string | null) {
  if (!start && !end) return "none"; // no subscription configured → allow
  
  const todayStr = getLocalTodayStr();
  const startStr = start ? start.slice(0, 10) : null;
  const endStr = end ? end.slice(0, 10) : null;
  
  if (startStr && startStr > todayStr) return "not_started";
  if (endStr && endStr < todayStr) return "expired";
  return "active";
}

/* ── animated ring ───────────────────────────────────────── */
function Ring({ expired }: { expired: boolean }) {
  return (
    <div className="relative flex items-center justify-center w-28 h-28 mx-auto mb-6">
      {/* outer pulse */}
      <div
        className="absolute inset-0 rounded-full opacity-20 animate-ping"
        style={{ background: expired ? "#ef4444" : "#f59e0b" }}
      />
      {/* inner circle */}
      <div
        className="relative w-24 h-24 rounded-full flex items-center justify-center shadow-2xl"
        style={{
          background: expired
            ? "linear-gradient(135deg,#991b1b,#ef4444)"
            : "linear-gradient(135deg,#78350f,#f59e0b)",
        }}
      >
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          {expired ? (
            <>
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </>
          ) : (
            <>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </>
          )}
        </svg>
      </div>
    </div>
  );
}

/* ── date badge ───────────────────────────────────────────── */
function DateBadge({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`flex flex-col items-center px-5 py-3 rounded-xl border ${highlight ? "border-red-500/40 bg-red-500/10" : "border-white/10 bg-white/5"}`}>
      <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1">{label}</span>
      <span className={`text-sm font-semibold ${highlight ? "text-red-300" : "text-white/80"}`}>{value}</span>
    </div>
  );
}

/* ── main gate ────────────────────────────────────────────── */
export function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { user, refreshUser, refreshUserBackground, loading } = useAuth();
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  const isDevUser = user?.email === "dev@developer.local" || user?.username === "dev";

  const start = user?.subscriptionStartDate;
  const end   = user?.subscriptionEndDate;
  const status = getStatus(start, end);

  console.log("🔒 [SubscriptionGate] evaluation:", {
    email: user?.email,
    username: user?.username,
    start,
    end,
    status,
    isDevUser,
    today: getLocalTodayStr()
  });

  // Silently refetch user details on every route/page navigation
  // to detect updated subscription status in real-time.
  useEffect(() => {
    if (!isDevUser && user) {
      refreshUserBackground();
    }
  }, [location.pathname, isDevUser]);

  /* Animate in */
  useEffect(() => {
    if (status !== "active" && status !== "none" && !isDevUser) {
      const t = setTimeout(() => setVisible(true), 80);
      return () => clearTimeout(t);
    }
  }, [status, isDevUser]);

  /* Dev user and "none" (no dates configured) always pass through */
  if (isDevUser || status === "active" || status === "none") {
    return (
      <>
        {/* Floating Debug Banner to identify gate state */}
        {isDevUser && (
          <div 
            id="subscription-gate-debug-banner"
            style={{
              position: 'fixed',
              bottom: '12px',
              right: '12px',
              background: 'rgba(15, 23, 42, 0.95)',
              color: '#34d399',
              padding: '12px 16px',
              borderRadius: '12px',
              fontSize: '11px',
              fontFamily: 'monospace',
              zIndex: 99999,
              border: '1px solid rgba(52, 211, 153, 0.3)',
              pointerEvents: 'none',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              flexDirection: 'column',
              gap: '3px'
            }}
          >
            <div style={{ fontWeight: 'bold', borderBottom: '1px solid rgba(52, 211, 153, 0.2)', paddingBottom: '3px', marginBottom: '3px' }}>
              🔒 Subscription Gate Debug
            </div>
            <div>User: {user?.email || 'N/A'}</div>
            <div>Status: <span style={{ color: status === 'active' ? '#34d399' : '#f59e0b', fontWeight: 'bold' }}>{status}</span></div>
            <div>Start: {start || 'null'}</div>
            <div>End: {end || 'null'}</div>
            <div>Today: {getLocalTodayStr()}</div>
          </div>
        )}
        {children}
      </>
    );
  }


  const expired     = status === "expired";
  const notStarted  = status === "not_started";
  const remaining   = daysLeft(end);

  const headline = expired
    ? "Subscription Expired"
    : "Subscription Not Yet Active";

  const subtext = expired
    ? "Your access period has ended. Please contact your administrator to renew the subscription."
    : "Your subscription hasn't started yet. Access will be unlocked on your activation date.";

  const orgName = user?.tenant?.organizationName || "Your Organization";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden">
      {/* Dark blurred backdrop */}
      <div className="absolute inset-0 bg-[#0a0f1e]" />

      {/* Radial gradient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: expired
            ? "radial-gradient(ellipse 60% 50% at 50% 40%, rgba(239,68,68,0.12) 0%, transparent 70%)"
            : "radial-gradient(ellipse 60% 50% at 50% 40%, rgba(245,158,11,0.12) 0%, transparent 70%)",
        }}
      />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Card */}
      <div
        className="relative z-10 mx-4 w-full max-w-md transition-all duration-500"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.97)",
        }}
      >
        <div
          className="rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
          style={{ background: "rgba(15,20,40,0.9)", backdropFilter: "blur(24px)" }}
        >
          {/* Top accent bar */}
          <div
            className="h-1 w-full"
            style={{
              background: expired
                ? "linear-gradient(90deg,#7f1d1d,#ef4444,#7f1d1d)"
                : "linear-gradient(90deg,#78350f,#f59e0b,#78350f)",
            }}
          />

          <div className="px-8 py-8 text-center">

            {/* Icon ring */}
            <Ring expired={expired} />

            {/* Org name pill */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/8 border border-white/10 mb-4">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-400">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <span className="text-xs font-semibold text-white/60">{orgName}</span>
            </div>

            {/* Headline */}
            <h2 className={`text-2xl font-black tracking-tight mb-2 ${expired ? "text-red-400" : "text-amber-400"}`}>
              {headline}
            </h2>

            {/* Subtext */}
            <p className="text-sm text-white/50 leading-relaxed mb-6 max-w-xs mx-auto">
              {subtext}
            </p>

            {/* Date chips */}
            <div className="flex gap-3 justify-center mb-6">
              <DateBadge label="Started" value={fmt(start)} />
              <DateBadge label="Expires" value={fmt(end)} highlight={expired} />
            </div>

            {/* Days remaining / overdue */}
            {end && (
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold mb-6 border
                  ${expired
                    ? "bg-red-500/10 border-red-500/20 text-red-300"
                    : "bg-amber-500/10 border-amber-500/20 text-amber-300"
                  }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                {expired
                  ? `Expired ${Math.abs(remaining ?? 0)} day${Math.abs(remaining ?? 0) !== 1 ? "s" : ""} ago`
                  : `Starts in ${remaining} day${remaining !== 1 ? "s" : ""}`}
              </div>
            )}

            {/* Re-verify Subscription button */}
            <button
              onClick={() => refreshUser()}
              disabled={loading}
              className="mt-2 mb-6 w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-600/50 text-white font-bold text-sm rounded-xl transition-all duration-200 shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Re-verifying Access...
                </>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                  </svg>
                  Check Subscription Status
                </>
              )}
            </button>

            {/* Divider */}
            <div className="h-px bg-white/8 mb-6" />

            {/* Contact hint */}
            <div className="flex items-start gap-3 text-left bg-white/4 border border-white/8 rounded-xl px-4 py-3">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-400 mt-0.5 flex-shrink-0">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.08 6.08l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              <div>
                <p className="text-[11px] font-bold text-white/70 uppercase tracking-wider">Need Help?</p>
                <p className="text-xs text-white/40 mt-0.5">Contact your PetroOne administrator to renew or activate your subscription.</p>
              </div>
            </div>

          </div>
        </div>

        {/* PetroOne branding below card */}
        <p className="text-center text-[10px] text-white/20 font-semibold tracking-widest uppercase mt-4">
          PetroOne · Fuel Station Management
        </p>
      </div>
    </div>
  );
}
